import { UseFormReturn } from 'react-hook-form';

import React, { useEffect, useState } from 'react';
import { MiscellaneousField } from '../../reportsTypes';
import { FieldEnabledState } from '../Miscellaneous/MiscellaneousStringItem';
import { ReportCloneValues } from '../utils/createReportCloneFormSchema';
import { groupFieldsByDependency } from '../utils/groupedFields';
import { FieldGroupRenderer } from '../Miscellaneous/FieldGroupRenderer';

interface MiscellaneousFormFieldProps {
  form: UseFormReturn<ReportCloneValues>;
  miscellaneousFields: Record<string, MiscellaneousField>;
}

export const MiscellaneousFormField = ({
  form,
  miscellaneousFields,
}: MiscellaneousFormFieldProps) => {
  const [watchedValues, setWatchedValues] = useState<Record<string, any>>(() =>
    form.getValues()
  );

  const [fieldEnabledState, setFieldEnabledState] = useState<FieldEnabledState>(
    () => {
      const initialState: FieldEnabledState = {};
      Object.keys(miscellaneousFields).forEach((fieldName) => {
        const fieldInfo = miscellaneousFields[fieldName];
        initialState[fieldName] =
          !fieldInfo.enabledOn ||
          (fieldInfo.enabledOn &&
            (form.getValues()['miscellaneous'] as any)?.[
              fieldInfo.enabledOn.field
            ] === fieldInfo.enabledOn.value);
      });
      return initialState;
    }
  );

  useEffect(() => {
    const subscription = form.watch((values: Partial<ReportCloneValues>) => {
      setWatchedValues(values as Record<string, any>);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    let hasChanged = false;
    const updatedState: FieldEnabledState = { ...fieldEnabledState };

    Object.keys(miscellaneousFields).forEach((fieldName) => {
      const fieldInfo = miscellaneousFields[fieldName];

      // Check if watchedValues has the key and if it's equal to the expected value
      const isEnabled =
        !fieldInfo.enabledOn ||
        (fieldInfo.enabledOn &&
          (watchedValues['miscellaneous'] as any)?.[
            fieldInfo.enabledOn.field
          ] === fieldInfo.enabledOn.value);

      if (updatedState[fieldName] !== isEnabled) {
        updatedState[fieldName] = isEnabled;
        hasChanged = true;

        if (!isEnabled && fieldEnabledState[fieldName]) {
          form.setValue('miscellaneous.' + fieldName, null);
        } else {
          form.setValue('miscellaneous.' + fieldName, fieldInfo.defaultValue);
        }
      }
    });

    if (hasChanged) {
      setFieldEnabledState(updatedState);
    }
  }, [form, watchedValues, fieldEnabledState, miscellaneousFields]);

  const groupedFields = groupFieldsByDependency(miscellaneousFields);

  return (
    <div className="space-y-6">
      {groupedFields.length > 0 &&
        groupedFields.map((group) => (
          <FieldGroupRenderer
            key={group.join('-')}
            group={group}
            miscellaneousFields={miscellaneousFields}
            fieldEnabledState={fieldEnabledState}
            form={form}
            prefix={true}
            dateRange={undefined}
          />
        ))}
    </div>
  );
};
